import {ChangeEvent, useEffect, useState} from 'react';
import styled from 'styled-components';
import {useMyAccessRestriction} from '@/api/access-restriction';
import {useUpdateMarketingConsent, useWithdrawUser} from '@/api/mypage';
import {useRefetchMe} from '@/api/session';
import {Button} from '@/components/Button';
import {Dialog} from '@/components/Dialog';
import toast from '@/components/HotToast';
import {NavBar} from '@/components/NavBar';
import {ToggleSwitch} from '@/components/ToggleSwitch';
import {Typography, typographyStyle} from '@/components/Typography';
import {UpdatePassword} from '@/components/UserSession/UpdatePassword';
import {AccessRestrictionType} from '@/enums';
import {useBreakpoints, useSession} from '@/hooks';
import {CHANNEL_TALK} from '@/router';
import {
  ColumnFlexBox,
  Flexbox,
  FlexFullGrow,
  HorizontalFullContainer,
  LinkText,
  PaddingBox,
  PageContent,
  ScrollBody,
  SimpleListBox,
} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import dayjs from 'dayjs';
import {addDashPhoneNumber} from '@/utils';

const MyAccount = () => {
  const {isMobile} = useBreakpoints();
  const refetchMe = useRefetchMe();

  useEffect(() => {
    refetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HorizontalFullContainer>
      <NavBar />
      <ScrollBody>
        <Content>
          <Typography variant={isMobile ? 'bold18' : 'bold24'}>
            개인 정보 변경
          </Typography>
          <Form />
        </Content>
      </ScrollBody>
    </HorizontalFullContainer>
  );
};

const Form = () => {
  const {user, isPaidUser} = useSession();
  const [isOpenPwdForm, setOpenPwdForm] = useState<boolean>(false);
  const [isOpenWithdrawBlocked, setOpenWithdrawBlocked] =
    useState<boolean>(false);
  const [isOpenWithdrawConfirm, setOpenWithdrawConfirm] =
    useState<boolean>(false);

  const {mutate, isPending} = useUpdateMarketingConsent();
  const {mutate: withdrawUser, isPending: isWithdrawPending} =
    useWithdrawUser();
  const {refetch: fetchAccessRestriction} = useMyAccessRestriction(
    user?.id,
    false,
  );

  const showSuccessToast = () => toast.success('변경사항이 저장되었습니다.');

  const onToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    mutate(checked, {
      onSuccess: (data) => {
        if (data?.result?.id) {
          showSuccessToast();
        }
      },
    });
  };

  const onPwdComplete = () => {
    setOpenPwdForm(false);
    showSuccessToast();
  };

  const onWithdrawClick = async () => {
    if (!isPaidUser) {
      setOpenWithdrawConfirm(true);
      return;
    }

    // 계정 제한 중이면 이용 기간이 남아 있어도 탈퇴할 수 있다.
    const {data: restriction} = await fetchAccessRestriction();

    if (restriction === AccessRestrictionType.Account) {
      setOpenWithdrawConfirm(true);
    } else {
      setOpenWithdrawBlocked(true);
    }
  };

  const handleWithdraw = () => {
    withdrawUser(
      {reason: 'Other', reasonEtc: '계정 정보 페이지를 통한 탈퇴'},
      {
        onSuccess: (data) => {
          if (data?.result?.needCancelSubscription) {
            setOpenWithdrawConfirm(false);
            setOpenWithdrawBlocked(true);
          } else if (data?.result?.success) {
            toast.success('탈퇴처리가 완료되었습니다.');
            setTimeout(() => window.location.replace('/intro'), 800);
          } else {
            toast.error(
              data?.result?.message ?? '탈퇴 처리 중 오류가 발생했습니다.',
            );
          }
        },
        onError: () => {
          toast.error('탈퇴 처리 중 오류가 발생했습니다.');
        },
      },
    );
  };

  if (!user) {
    return null;
  }

  return (
    <ColumnFlexBox gap={24}>
      <Flexbox>
        <Label>이름</Label>
        <FlexFullGrow />
        <Value>{user.nickname}</Value>
      </Flexbox>
      <Flexbox>
        <Label>이메일 주소</Label>
        <FlexFullGrow />
        <Value>{user.email}</Value>
      </Flexbox>
      <Flexbox>
        <Label>휴대전화 번호</Label>
        <FlexFullGrow />
        <Value>{addDashPhoneNumber(user.cellPhone)}</Value>
      </Flexbox>
      <Flexbox>
        <Label>비밀번호</Label>
        <FlexFullGrow />
        <Button
          size="regular"
          color="black"
          variant="outline"
          onClick={() => setOpenPwdForm(true)}
        >
          변경하기
        </Button>
      </Flexbox>
      <Flexbox alignItems="flex-start">
        <div style={{maxWidth: 214}}>
          <Label>마케팅 정보 수신 동의(선택)</Label>
          <PaddingBox top={9}>
            <Typography variant="medium12" color={palette.gray600}>
              수신 동의 시 이벤트 및 각종 행사 등의 정보를 알려드립니다.
            </Typography>
          </PaddingBox>
        </div>
        <FlexFullGrow />
        <ToggleSwitch
          checked={user.isMarketingAgreed}
          disabled={isPending}
          onChange={onToggle}
        />
      </Flexbox>
      <FlexFullGrow />
      <PaddingBox bottom={16}>
        <SimpleListBox>
          <ul>
            <li>
              이메일 주소, 휴대전화 번호 정보 수정을 희망하시는 경우{' '}
              <LinkText href={CHANNEL_TALK} target="_blank">
                고객센터
              </LinkText>
              로 연락부탁드립니다.
            </li>
            <li>
              <WithdrawButton onClick={onWithdrawClick}>
                서비스 탈퇴하기
              </WithdrawButton>
            </li>
          </ul>
        </SimpleListBox>
      </PaddingBox>
      {isOpenPwdForm && (
        <UpdatePassword
          onClose={() => setOpenPwdForm(false)}
          onComplete={onPwdComplete}
        />
      )}
      {isOpenWithdrawBlocked && (
        <Dialog
          title="탈퇴 불가"
          confirmLabel="확인"
          confirmOptions={{color: 'secondary', fullWidth: true}}
          cancelOptions={{hidden: true}}
          onConfirm={() => setOpenWithdrawBlocked(false)}
          onClose={() => setOpenWithdrawBlocked(false)}
        >
          <ColumnFlexBox gap={12}>
            <span>
              현재 이용 중인 요금제가 있습니다.
              <br />
              이용 중인 요금제 기간 만료 후 탈퇴 가능합니다.
            </span>
            <SimpleList>
              <ul>
                {user.billing?.orderName && (
                  <li>이용 중 요금제 : {user.billing.orderName}</li>
                )}
                {user.useEndedAt && (
                  <li>
                    만료 일시 :{' '}
                    {dayjs(user.useEndedAt).format('YYYY-MM-DD HH:mm')}
                  </li>
                )}
              </ul>
            </SimpleList>
          </ColumnFlexBox>
        </Dialog>
      )}
      {isOpenWithdrawConfirm && (
        <Dialog
          title="탈퇴하기"
          cancelLabel="아니요"
          confirmLabel="네"
          confirmOptions={{color: 'secondary', disabled: isWithdrawPending}}
          onCancel={() => setOpenWithdrawConfirm(false)}
          onConfirm={handleWithdraw}
          onClose={() => setOpenWithdrawConfirm(false)}
        >
          탈퇴하시면 더 이상 서비스 활용이 불가능합니다.
          <br />
          탈퇴하시겠습니까?
        </Dialog>
      )}
    </ColumnFlexBox>
  );
};

export default MyAccount;

const Content = styled(PageContent)`
  gap: 24px;
  ${MEDIA_QUERIES.exclude_mobile} {
    min-height: 530px;
  }
  min-height: 100%;
`;

const Label = styled.label`
  ${{...typographyStyle.bold16}}
`;

const Value = styled.div`
  ${{...typographyStyle.medium16}}
  color: ${palette.gray600};
`;

const WithdrawButton = styled.span`
  text-decoration: underline;
  cursor: pointer;
  line-height: 37px;
`;

const SimpleList = styled(SimpleListBox)`
  font-size: 14px;
  color: ${palette.gray600};
`;
